import React, {useEffect, useState} from 'react';
import axios from "axios";
import {useParams} from "react-router-dom";
import {enqueueSnackbar} from "notistack";
import {LoaderType, Loading} from "@/layouts/buddy/loading";

interface ICandidateSummaryItem {
    number: string;
    label: string;
    onClick?: () => void;
}

interface ICandidateSummaryItems extends Array<ICandidateSummaryItem> {
}

interface ICandidateSummary {
    matched_jobs: number;
    // Add additional summary fields as needed
}

interface CandidateSummaryProps {
    setActivePage: (page: string) => void;
    onSummaryFetched?: (matchedJobs: number) => void;
    loadingMatches: boolean;
}

const CandidateSummary: React.FC<CandidateSummaryProps> = ({setActivePage, onSummaryFetched, loadingMatches}) => {
    const {p_id} = useParams(); // Access candidate p_id from URL

    const [items, setItems] = useState<ICandidateSummaryItems>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    useEffect(() => {
        if (!p_id) {
            enqueueSnackbar(`No candidate id provided`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
            return;
        }

        const fetchCandidateSummary = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get<ICandidateSummary>(`${API_URL}/candidate/${p_id}/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                const {matched_jobs} = response.data;

                if (onSummaryFetched) {
                    onSummaryFetched(matched_jobs);
                }

                const candidateSummaryItems: ICandidateSummaryItems = [
                    {
                        number: matched_jobs.toString(),
                        label: 'Matched Jobs',
                        onClick: () => setActivePage('jobs'), // Set active page when clicked
                    }
                    // Add more summary items here if needed
                ];

                setItems(candidateSummaryItems);
            } catch (err: any) {
                console.error('Error fetching candidate summary:', err);
                if (err.response && err.response.status === 404) {
                    enqueueSnackbar(`Candidate not found or access denied.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else if (err.response && err.response.status === 401) {
                    enqueueSnackbar(`Unauthorized. Please log in.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else {
                    enqueueSnackbar(`Failed to fetch candidate summary.`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        fetchCandidateSummary();
    }, [API_URL, p_id, setActivePage, onSummaryFetched]);

    const renderItems = (item: ICandidateSummaryItem, index: number) => (
        <React.Fragment key={index}>
            <button
                onClick={item.onClick}
                className={`flex flex-col items-center flex-1 gap-1 text-center focus:outline-none 
                           ${item.onClick ? 'cursor-pointer hover:text-primary' : ''}`}
            >
                <span className="text-gray-900 text-2xl lg:text-3xl leading-none font-semibold hover:text-primary">
                    {item.number}
                </span>
                <span className="text-gray-700 text-sm hover:text-primary">{item.label}</span>
            </button>
            {index !== items.length - 1 && (
                <span className="border-r border-r-gray-300 my-1 h-full"></span>
            )}
        </React.Fragment>
    );

    if (loading) return (
        <div className="card">
            <div className="card-body">
                <div className="flex lg:px-10 py-1.5 gap-2">
                    <Loading loading={loading} loadingText={"Loading Summary"}/>
                </div>
            </div>
        </div>
    )

    if (loadingMatches) return (
        <div className="card">
            <div className="card-body">
                <div className="flex lg:px-10 py-1.5 gap-2">
                    <Loading loading={loadingMatches} loaderType={LoaderType.ROBOT_PRO_CON} loadingText={"Processing Matches"} delay={0}/>
                </div>
            </div>
        </div>
    )

    return (
        <div className="card">
            <div className="card-body">
                <div className="flex lg:px-10 py-1.5 gap-2">
                    {items.map((item, index) => renderItems(item, index))}
                </div>
            </div>
        </div>
    );
};

export {CandidateSummary, type ICandidateSummaryItem, type ICandidateSummaryItems};
